import {
  CartDiscountUpdateEvent,
  CartErrorEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
} from '@theme/standard-events';
import { fetchTextWithTimeout, getCartSections } from '@scale/utilities';
import { morphSection } from '@scale/section-renderer';

const JSON_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};

const STANDARD_EVENT_SOURCE = 'scale-cart-api';

export class CartAPI {
  static #pending = new Map();

  static add(items, { sections = [], target, context } = {}) {
    if (!Array.isArray(items)) items = [items];
    return CartAPI.#addAndRefresh(items, sections, { target, context });
  }

  static change(key, quantity, { sections = [], target, context = 'cart', ready } = {}) {
    return CartAPI.#request('change', `${routes().cart_change_url}.js`, {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity, sections: sections.join(',') }),
    }, sections, {
      EventClass: CartLinesUpdateEvent,
      target,
      payload: {
        action: quantity === 0 ? 'remove' : 'update',
        context,
        lines: [{ id: key, quantity }],
      },
    }, ready);
  }

  static update(updates, { sections = [], target, context = 'cart' } = {}) {
    return CartAPI.#request('update', `${routes().cart_url}/update.js`, {
      method: 'POST',
      body: JSON.stringify({ updates, sections: sections.join(',') }),
    }, sections, {
      EventClass: CartLinesUpdateEvent,
      target,
      payload: {
        action: 'update',
        context,
        lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
      },
    });
  }

  static async clear({ sections = [], target, context = 'cart' } = {}) {
    const currentCart = await CartAPI.get();
    if (!currentCart?.items?.length) return currentCart;

    return CartAPI.#request('clear', `${routes().cart_url}/clear.js`, {
      method: 'POST',
      body: JSON.stringify({ sections: sections.join(',') }),
    }, sections, {
      EventClass: CartLinesUpdateEvent,
      target,
      payload: {
        action: 'remove',
        context,
        lines: currentCart.items.map((item) => ({
          id: item.key || item.id,
          quantity: 0,
        })),
      },
    });
  }

  static async get() {
    CartAPI.#pending.get('get')?.abort();
    const controller = new AbortController();
    CartAPI.#pending.set('get', controller);

    try {
      const { response, text } = await fetchTextWithTimeout(`${routes().cart_url}.js`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) throw responseError(response, text);
      return JSON.parse(text);
    } finally {
      if (CartAPI.#pending.get('get') === controller) CartAPI.#pending.delete('get');
    }
  }

  static applyDiscount(code, { sections = [], target } = {}) {
    const discountCodes = String(code)
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value) => ({ code: value }));

    return CartAPI.#request('discount', `${routes().cart_url}/update.js`, {
      method: 'POST',
      body: JSON.stringify({ discount: code, sections: sections.join(',') }),
    }, sections, {
      EventClass: CartDiscountUpdateEvent,
      target,
      payload: { discountCodes },
    });
  }

  static removeDiscount(options = {}) {
    return CartAPI.applyDiscount('', options);
  }

  static updateNote(note, { target, context = 'cart' } = {}) {
    return CartAPI.#request('note', `${routes().cart_url}/update.js`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }, [], {
      EventClass: CartNoteUpdateEvent,
      target,
      payload: { context, note },
    });
  }

  static async #addAndRefresh(items, sections, { target, context }) {
    CartAPI.#pending.get('add')?.abort();
    const controller = new AbortController();
    CartAPI.#pending.set('add', controller);
    const eventTarget = resolveEventTarget(target);
    const deferred = dispatchStandardEvent(CartLinesUpdateEvent, eventTarget, {
      action: 'add',
      context: context || inferContext(eventTarget, 'product'),
      lines: items.map((item) => ({
        merchandiseId: item.id,
        quantity: item.quantity,
      })),
    });

    try {
      const { response: addResponse, text: addText } = await fetchTextWithTimeout(`${routes().cart_add_url}.js`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ items, sections: sections.join(',') }),
        signal: controller.signal,
        timeout: 15000,
      });
      if (!addResponse.ok) throw responseError(addResponse, addText);
      const added = JSON.parse(addText);

      const { response: cartResponse, text: cartText } = await fetchTextWithTimeout(`${routes().cart_url}.js`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        signal: controller.signal,
        timeout: 15000,
      });
      if (!cartResponse.ok) throw responseError(cartResponse, cartText);

      const cart = JSON.parse(cartText);
      cart.sections = added.sections || {};
      renderCartSections(cart.sections, sections);
      deferred.resolve(standardResult(cart));
      return cart;
    } catch (caught) {
      const error = shopperError(caught);
      deferred.reject(error);
      if (error.name !== 'AbortError') dispatchCartError(eventTarget, error);
      throw error;
    } finally {
      if (CartAPI.#pending.get('add') === controller) CartAPI.#pending.delete('add');
    }
  }

  static async #request(source, url, options, sections, eventConfig, ready) {
    CartAPI.#pending.get(source)?.abort();
    const controller = new AbortController();
    CartAPI.#pending.set(source, controller);
    const eventTarget = resolveEventTarget(eventConfig.target);
    const deferred = dispatchStandardEvent(
      eventConfig.EventClass,
      eventTarget,
      eventConfig.payload,
    );

    try {
      const { response, text } = await fetchTextWithTimeout(url, {
        ...options,
        headers: { ...JSON_HEADERS, ...(options.headers || {}) },
        signal: controller.signal,
        timeout: 15000,
      });
      if (!response.ok) throw responseError(response, text);

      const cart = JSON.parse(text);
      await ready;
      renderCartSections(cart.sections, sections);
      deferred.resolve(standardResult(cart));
      return cart;
    } catch (caught) {
      const error = shopperError(caught);
      deferred.reject(error);
      if (error.name !== 'AbortError') dispatchCartError(eventTarget, error);
      throw error;
    } finally {
      if (CartAPI.#pending.get(source) === controller) CartAPI.#pending.delete(source);
    }
  }
}

export async function refreshCartSections({ sections = getCartSections(), signal } = {}) {
  if (!sections.length) return {};

  const url = new URL(window.location.href);
  url.searchParams.set('sections', sections.join(','));
  const { response, text } = await fetchTextWithTimeout(url, {
    headers: { 'Accept': 'application/json' },
    signal,
  });
  if (!response.ok) throw responseError(response, text);

  const rendered = JSON.parse(text);
  renderCartSections(rendered, sections);
  return rendered;
}

export async function handleStandardCartUpdateAction(
  defaultHandler,
  refreshSections = refreshCartSections,
) {
  const result = await defaultHandler();
  if (!result.userErrors?.length) {
    try {
      await refreshSections();
    } catch (error) {
      console.error('Cart updated, but storefront sections could not be refreshed.', error);
    }
  }
  return {
    ...result,
    detail: { ...result.detail, source: 'scale-standard-action' },
  };
}

function dispatchStandardEvent(EventClass, target, payload) {
  const deferred = EventClass.createPromise();
  void deferred.promise.catch(() => {});
  target.dispatchEvent(new EventClass({
    ...payload,
    promise: deferred.promise,
    detail: { source: STANDARD_EVENT_SOURCE },
  }));
  return deferred;
}

function shopperError(error) {
  if (error?.status || error?.name === 'AbortError') return error;
  return new Error(Scale.translations.cartError, { cause: error });
}

function dispatchCartError(target, error) {
  target.dispatchEvent(new CartErrorEvent({
    error: error.message,
    code: error.status >= 400 && error.status < 500 ? 'INVALID' : 'SERVICE_UNAVAILABLE',
  }));
}

function standardResult(ajaxCart) {
  return {
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
    detail: { source: STANDARD_EVENT_SOURCE },
  };
}

function renderCartSections(rendered = {}, requested = []) {
  for (const sectionId of requested) {
    const html = rendered?.[sectionId];
    if (html) morphSection(sectionId, html);
  }
}

function resolveEventTarget(target) {
  if (target?.dispatchEvent) return target;
  return document.activeElement?.closest?.(
    'product-form, scale-quick-add, scale-cart-item, scale-cart-note, scale-cart-discount, scale-cart-drawer, scale-cart-page',
  ) || document.querySelector('scale-cart-drawer, scale-cart-page') || document;
}

function inferContext(target, fallback = 'cart') {
  if (target?.closest?.('dialog, scale-cart-drawer')) return 'dialog';
  if (target?.closest?.('product-form, scale-quick-add')) return 'product';
  return fallback;
}

function responseError(response, text) {
  let message = Scale.translations.cartError;
  try {
    const payload = JSON.parse(text);
    message = payload.description || payload.message || message;
  } catch {   }
  const error = new Error(message);
  error.status = response.status;
  return error;
}

function routes() {
  return window.Scale?.routes || {
    cart_add_url: '/cart/add',
    cart_change_url: '/cart/change',
    cart_url: '/cart',
  };
}
